import type { FunctionComponent } from "react";
import { useMemo } from "react";

import { notEmpty } from "shared/lib/utils";

import { DefaultContentTypes } from "scmp-app/components/search/consts";
import { useSearchContext } from "scmp-app/components/search/contexts";
import { DropdownMenu } from "scmp-app/components/search/search-result-panel/components/dropdown-menu";
import type { hooksTypeMenuArticleSearchConnection$key } from "scmp-app/queries/__generated__/hooksTypeMenuArticleSearchConnection.graphql";
import type { ContentTypeEnum } from "scmp-app/queries/__generated__/searchArticleResultPaginationQuery.graphql";

import type { TypeOption } from "./const";
import { useTypeMenuOptions } from "./hooks";

type Props = {
  className?: string;
  reference: hooksTypeMenuArticleSearchConnection$key;
};

export const TypeMenu: FunctionComponent<Props> = ({ className, reference }) => {
  const { articleTypeIds, contentTypes, setArticleTypeIds, setArticleTypeText, setContentTypes } =
    useSearchContext();
  const { options } = useTypeMenuOptions(reference);

  const isChangedOptionColor = useMemo(() => {
    if (notEmpty(articleTypeIds)) return true;
    return (
      !Array.isArray(contentTypes) ||
      contentTypes.length !== DefaultContentTypes.length ||
      !contentTypes.every(type => DefaultContentTypes.includes(type))
    );
  }, [articleTypeIds, contentTypes]);

  const handleOptionSelect = (typeOption: TypeOption) => {
    if (typeOption.isContentType) {
      setArticleTypeIds?.([]);
      setContentTypes?.(typeOption.value as ContentTypeEnum[]);
    } else {
      setArticleTypeIds?.(typeOption.value);
      setContentTypes?.([]);
    }
    setArticleTypeText?.(typeOption.text);
  };

  return (
    <DropdownMenu<string[], TypeOption>
      className={className}
      isChangedOptionColor={isChangedOptionColor}
      onOptionSelect={handleOptionSelect}
      options={options}
      placeholder="Type"
      selectedOptionValue={notEmpty(articleTypeIds) ? articleTypeIds : contentTypes}
      withCount={!!options}
    />
  );
};

TypeMenu.displayName = "TypeMenu";
